import loadable from '@loadable/component';
import React from 'react';
import {
  createBrowserRouter,
  createHashRouter,
  matchRoutes,
  redirect,
  replace,
  useRouteError,
} from 'react-router';

import config from './config';
import { openNewsletterConfirmedModal } from './ducks/newsletter.duck';
import { locationChanged } from './ducks/routing.duck';
import LocaleLayout from './layouts/LocaleLayout';
import PageWrapper from './layouts/PageWrapper';
import RootLayout from './layouts/RootLayout';
import routeConfiguration, { fixedLocaleRoutes } from './routeConfiguration';
import { whenAnalyticsReady } from './util/consent/initConsent';
import { logError } from './util/log';
import { isCordova } from './util/mobile/deviceDetection';
import { canonicalRoutePath } from './util/routes';

const NotFoundPage = loadable(
  () => import(/* webpackChunkName: "NotFoundPage" */ './containers/NotFoundPage/NotFoundPage')
);

// Translations for the error boundary (can't use IntlProvider — it's above us)
const errorTranslations = {
  de: { heading: 'Hoppla', message: 'Etwas ist schiefgelaufen. Bitte versuche es erneut oder kehre zur Startseite zurück.', reload: 'Seite neu laden', goHome: 'Zur Startseite' },
  en: { heading: 'Oops', message: 'Something went wrong. Please try again or return to the homepage.', reload: 'Reload Page', goHome: 'Go to Homepage' },
  es: { heading: 'Ups', message: 'Algo salió mal. Inténtalo de nuevo o vuelve a la página de inicio.', reload: 'Recargar página', goHome: 'Ir a la página de inicio' },
  fr: { heading: 'Oups', message: 'Une erreur est survenue. Veuillez réessayer ou retourner à l\'accueil.', reload: 'Recharger la page', goHome: 'Aller à l\'accueil' },
  it: { heading: 'Ops', message: 'Qualcosa è andato storto. Riprova o torna alla pagina iniziale.', reload: 'Ricarica la pagina', goHome: 'Vai alla homepage' },
  no: { heading: 'Oops', message: 'Noe gikk galt. Prøv igjen eller gå tilbake til hjemmesiden.', reload: 'Last siden på nytt', goHome: 'Gå til hjemmesiden' },
  sv: { heading: 'Hoppsan', message: 'Något gick fel. Försök igen eller gå tillbaka till startsidan.', reload: 'Ladda om sidan', goHome: 'Gå till startsidan' },
};

const getErrorLocale = () => {
  if (typeof window === 'undefined') return 'en';
  const match = window.location.pathname.match(/^\/([a-z]{2})(\/|$)/);
  return (match && errorTranslations[match[1]]) ? match[1] : 'en';
};

const RootErrorBoundary = () => {
  const error = useRouteError();
  const isDev = process.env.NODE_ENV === 'development';
  const t = errorTranslations[getErrorLocale()];
  const errorMessage = error?.statusText || error?.message || '';

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ maxWidth: 480, textAlign: 'center' }}>
        <h1 style={{ fontSize: 72, margin: 0, color: '#ccc' }}>{t.heading}</h1>
        <p style={{ color: '#666', fontSize: 18, marginBottom: 32 }}>{t.message}</p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          <button
            onClick={() => window.location.reload()}
            style={{ padding: '10px 24px', fontSize: 16, cursor: 'pointer', border: '1px solid #ccc', borderRadius: 4, background: '#fff' }}
          >
            {t.reload}
          </button>
          <button
            onClick={() => { window.location.href = '/'; }}
            style={{ padding: '10px 24px', fontSize: 16, cursor: 'pointer', border: 'none', borderRadius: 4, background: '#2ecc71', color: '#fff' }}
          >
            {t.goHome}
          </button>
        </div>
        {isDev ? <pre style={{ marginTop: 32, padding: 16, background: '#f5f5f5', borderRadius: 4, textAlign: 'left', fontSize: 12, overflow: 'auto', maxHeight: 300 }}>
            {errorMessage}
            {error?.stack ? `\n\n${error.stack}` : null}
          </pre> : null}
      </div>
    </div>
  );
};

const getLocaleSegment = (locale) => locale && locale.split('-')[0];
const isSupportedLocale = (locale) => !!config.languages[getLocaleSegment(locale)];

/**
 * Detect the preferred locale for a request.
 * Client: navigator.language. SSR: the Accept-Language header (set by app.js
 * from serverContext.locale). Falls back to config.fallbackLocale.
 */
const detectLocale = (request) => {
  if (typeof navigator !== 'undefined' && navigator.language) {
    const browserLocale = getLocaleSegment(navigator.language);
    if (isSupportedLocale(browserLocale)) return browserLocale;
  } else {
    const acceptLang = request.headers?.get?.('Accept-Language');
    if (acceptLang) {
      const primary = acceptLang.split(',')[0]?.split('-')[0]?.trim();
      if (isSupportedLocale(primary)) return primary;
    }
  }
  return config.fallbackLocale;
};

/**
 * Loader for the `:locale` route. When the first path segment is not a
 * supported locale (e.g. `/listing` or a typo like `/dx/page`), redirect to
 * the detected locale SERVER-SIDE via `redirect()` so the static handler emits
 * a real 302 instead of rendering <Navigate> on the initial StaticRouter pass.
 */
const createLocaleGuardLoader = () => {
  return ({ request, params }) => {
    const pathLocale = params.locale;
    if (!pathLocale || isSupportedLocale(pathLocale)) return null;

    const url = new URL(request.url);
    const fallback = detectLocale(request);
    // A 2-letter segment was likely an attempted locale (e.g. /dx/page) — swap it.
    // Otherwise it's a regular path missing its locale prefix — prepend the locale.
    const isLocaleLike = /^[a-z]{2}$/.test(pathLocale);
    const target = isLocaleLike
      ? url.pathname.replace(`/${pathLocale}`, `/${fallback}`)
      : `/${fallback}${url.pathname}`;
    throw redirect(`${target}${url.search}`);
  };
};

/**
 * Bridge: converts existing loadDuck/loadData pattern into an RR7 loader.
 * Loaders dispatch to Redux store. Components continue reading from Redux.
 */
const createRouteLoader = (route, store) => {
  return async ({ request, params }) => {
    const { auth, authPage = 'SigninPage', loadDuck } = route;
    const isSSR = typeof window === 'undefined';
    const state = store.getState();

    // Auth guard — CLIENT ONLY.
    // On SSR, skip auth check entirely (server handles auth via cookies post-render,
    // matching current dataLoader.js behavior).
    if (auth && !isSSR && !state.auth.isAuthenticated) {
      const locale = params.locale || config.fallbackLocale;
      const target = routeConfiguration().find((r) => r.name === authPage);
      const requestUrl = new URL(request.url);
      // The query string must survive the round-trip through the login page:
      // EmailVerificationPage carries its verification token in `?t=`, and
      // returning without it renders a form that can never be submitted.
      const from = `${requestUrl.pathname}${requestUrl.search}`;
      throw redirect(`/${locale}${target.path}?from=${encodeURIComponent(from)}`);
    }

    // On SSR, skip ALL data loading — data is pre-loaded by server/dataLoader.js
    // into the Redux store before renderApp is called.
    if (isSSR) return null;

    if (state.auth.logoutInProgress) return null;
    if (typeof loadDuck !== 'function') return null;

    // Load duck module (code-split)
    const { loadData } = await loadDuck();
    const url = new URL(request.url);

    // Apply per-route param transforms (e.g., TransactionPage adds transactionRole)
    const resolvedParams = route.paramsTransform ? route.paramsTransform(params) : params;

    // Fire-and-forget: dispatch loadData but DON'T await it.
    // This triggers the Redux thunk (sets fetchInProgress = true immediately),
    // allowing the component to render fast with a loading state while data
    // fetches in the background. Awaiting would block rendering until API completes.
    const match = { params: resolvedParams, path: url.pathname };
    store.dispatch(loadData(resolvedParams, url.search, match));

    // Newsletter modal trigger (client navigation; the hydrated first load is
    // handled in ClientApp because loaders don't run there)
    store.dispatch(openNewsletterConfirmedModal(url.search));

    return null;
  };
};

/**
 * Convert flat routeConfiguration() into nested RR7 data router route objects.
 */
const createFixedLocaleRoute = (route, store) => ({
  id: `fixed-${route.name}`,
  path: route.path,
  element: <LocaleLayout forcedLocale={route.renderLocale} />,
  children: [
    {
      id: route.name,
      index: true,
      element: <PageWrapper route={route} />,
      loader: createRouteLoader(route, store),
      handle: { name: route.name },
    },
  ],
});

const createDataRouterRoutes = (store) => {
  const routes = routeConfiguration();

  const pageRoutes = routes.map((route) => {
    const path = route.path.replace(/^\//, ''); // Remove leading / (relative to :locale)
    const isExact = route.exact != null ? route.exact : true;

    // Routes with path '/' become '' after stripping — use index route for RR7 data router
    if (path === '') {
      return {
        id: route.name,
        index: true,
        element: <PageWrapper route={route} />,
        loader: createRouteLoader(route, store),
        handle: { name: route.name, auth: route.auth },
      };
    }

    return {
      id: route.name,
      path: isExact ? path : `${path}/*`,
      element: <PageWrapper route={route} />,
      loader: createRouteLoader(route, store),
      handle: { name: route.name, auth: route.auth },
    };
  });

  return [
    {
      id: 'root',
      element: <RootLayout />,
      errorElement: <RootErrorBoundary />,
      HydrateFallback: () => null, // Suppress warning — SSR HTML is already present during hydration
      children: [
        {
          id: 'locale',
          path: ':locale',
          element: <LocaleLayout />,
          loader: createLocaleGuardLoader(),
          children: [...pageRoutes, { id: 'not-found', path: '*', element: <NotFoundPage /> }],
        },
        // Fixed pages under a path that is not a supported locale prefix (e.g. /nl/...).
        ...fixedLocaleRoutes().map((route) => createFixedLocaleRoute(route, store)),
        // URLs without locale prefix → redirect to detected locale
        {
          id: 'locale-redirect',
          path: '*',
          loader: ({ request }) => {
            const url = new URL(request.url);
            const locale = detectLocale(request);
            const target = `/${locale}${url.pathname}${url.search}`;
            // On Cordova (hash router) the app cold-starts at `/`, so this
            // redirect is the app's first navigation. A push would leave a
            // phantom `/` entry that just re-redirects forward — the Android
            // back button could then never reach the root to exit. Replace it
            // so the app starts at history index 0 (the true root).
            throw isCordova() ? replace(target) : redirect(target);
          },
        },
      ],
    },
  ];
};

const createWebRouter = (routes) => {
  // Pass hydration data injected by StaticRouterProvider during SSR.
  // Without this, the browser router re-runs all loaders on hydration,
  // shows HydrateFallback (null) while they resolve, and React tears down
  // the SSR HTML because the virtual DOM (null) mismatches the server HTML.
  let hydrationData = window.__staticRouterHydrationData;

  if (hydrationData && hydrationData.loaderData) {
    // Static HTML may have been generated for a different URL variant than the
    // current browser URL (e.g., canonical /l/:id served for slug /l/:slug/:id
    // via CloudFront). The hydration data's loaderData keys are route IDs from
    // the SSR route match, which won't match the client's route match.
    // Since all loaders return null, we can safely add null entries for any
    // matched routes missing from the hydration data.
    const matches = matchRoutes(routes, window.location.pathname);
    if (matches) {
      const loaderData = { ...hydrationData.loaderData };
      let modified = false;
      matches.forEach(({ route }) => {
        if (route.id && !(route.id in loaderData)) {
          loaderData[route.id] = null;
          modified = true;
        }
        // Auth routes are skipped by the server (dataLoader.js), so their loaderData
        // is always null. Remove these entries so React Router calls the client-side
        // loader, which handles auth redirect + loadData dispatch.
        if (route.handle?.auth && route.id in loaderData) {
          delete loaderData[route.id];
          modified = true;
        }
      });
      if (modified) {
        hydrationData = { ...hydrationData, loaderData };
      }
    }
  }

  return createBrowserRouter(routes, hydrationData ? { hydrationData } : undefined);
};

const isHashChangeOnly = (from, to) =>
  !!from && from.pathname === to.pathname && from.search === to.search && from.hash !== to.hash;

/**
 * Tracks every committed navigation. Route loaders run before React Router
 * commits the new URL, and the Meta Pixel reads document.location (its `dl`)
 * when fbq() runs, so tracking from the loader attributed PageView/Search to
 * the page the user just left. Subscribers run after the history update.
 * Revalidations keep the location key and hash-only changes run no loaders:
 * neither is a page view.
 *
 * @param {object} store Redux store
 * @param {object} router data router
 * @returns {() => void} unsubscribe
 */
const trackNavigations = (store, router) => {
  // A server-rendered landing page starts out initialized and is replayed by
  // trackLandingPage. Otherwise (Cordova, auth routes) the router initializes
  // on the client and its first committed location is the landing page.
  let tracked = router.state.initialized ? router.state.location : null;

  return router.subscribe(({ initialized, navigation, location, matches }) => {
    if (!initialized || navigation.state !== 'idle' || location.key === tracked?.key) return;
    const previous = tracked;
    tracked = location;
    const routeName = matches[matches.length - 1]?.route.handle?.name;
    // No page route (404)
    if (!routeName || isHashChangeOnly(previous, location)) return;

    const { pathname, search, hash } = location;
    const committed = { pathname, search, hash };
    try {
      store.dispatch(locationChanged(committed, canonicalRoutePath(routeConfiguration(), committed), routeName));
    } catch (e) {
      // A throwing subscriber would cut React Router's notification short and
      // leave the page on the old route. E.g. a malformed `?event=` param.
      logError(e, 'track-navigation-failed', { routeName });
    }
  });
};

const createAppRouter = (store) => {
  const routes = createDataRouterRoutes(store);
  const router = isCordova() ? createHashRouter(routes) : createWebRouter(routes);
  trackNavigations(store, router);
  return router;
};

/**
 * Hydration does not re-run route loaders, so the server-rendered landing page
 * never dispatches locationChanged: no Meta PageView/Search, no
 * view_search_results, no `?event=` events. Replays it once the analytics
 * vendors are ready, flagged so GA4 skips the page_view its SDK already sent.
 * Call once on mount, before the user can navigate.
 *
 * @param {object} store Redux store
 * @param {object} router data router from createAppRouter
 * @returns {Promise<void>} never rejects
 */
const trackLandingPage = async (store, router) => {
  // Cordova renders without hydration: its loaders run and track the location.
  if (isCordova() || !window.__staticRouterHydrationData) return;

  const { matches } = router.state;
  const route = matches[matches.length - 1]?.route;
  // No page route (404) or an auth route, whose loader runs on the client anyway.
  if (!route?.handle?.name || route.handle.auth) return;

  const { pathname, search, hash } = window.location;
  const location = { pathname, search, hash };

  await whenAnalyticsReady();
  // A client navigation already tracked a newer location.
  if (store.getState().routing.currentLocation) return;

  const canonical = canonicalRoutePath(routeConfiguration(), location);
  try {
    store.dispatch(locationChanged(location, canonical, route.handle.name, true));
  } catch (e) {
    // e.g. a malformed `?event=` param the analytics handlers fail to decode
    logError(e, 'track-landing-page-failed', { routeName: route.handle.name });
  }
};

export { createAppRouter, createDataRouterRoutes, trackLandingPage, trackNavigations };
